'use client';
import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { Button, Input, Card } from '@ui';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function fmt(d: Date | null) {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
}

function sameDay(a: Date | null, b: Date | null) {
  return (
    !!a &&
    !!b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function isBetween(d: Date, a: Date | null, b: Date | null) {
  if (!a || !b) return false;
  const x = startOfDay(d).getTime();
  const s = startOfDay(a).getTime();
  const e = startOfDay(b).getTime();
  return x > Math.min(s, e) && x < Math.max(s, e);
}

type Cell = { date: Date; out: boolean };

function buildCells(cursor: Date): Cell[] {
  const y = cursor.getFullYear();
  const m = cursor.getMonth();
  const first = new Date(y, m, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const prevDays = new Date(y, m, 0).getDate();

  const cells: Cell[] = [];
  for (let i = startDay - 1; i >= 0; i--) {
    const d = new Date(y, m - 1, prevDays - i);
    cells.push({ date: d, out: true });
  }
  for (let i = 1; i <= daysInMonth; i++) {
    const d = new Date(y, m, i);
    cells.push({ date: d, out: false });
  }
  while (cells.length % 7 !== 0) {
    const nextIndex = cells.length - startDay - daysInMonth + 1;
    const d = new Date(y, m + 1, nextIndex);
    cells.push({ date: d, out: true });
  }
  return cells;
}

const DateRangePicker = () => {
  const [cursor, setCursor] = useState<Date>(new Date());
  const [start, setStart] = useState<Date | null>(null);
  const [end, setEnd] = useState<Date | null>(null);
  const [focusedDate, setFocusedDate] = useState<Date | null>(null);

  const cells = useMemo(() => buildCells(cursor), [cursor]);

  const onPrev = useCallback(() => {
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  }, []);

  const onNext = useCallback(() => {
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
  }, []);

  const onCellClick = useCallback(
    (date: Date) => {
      setFocusedDate(date);
      if (!start || (start && end)) {
        setStart(date);
        setEnd(null);
      } else if (start && !end) {
        if (start.getTime() > date.getTime()) {
          setEnd(start);
          setStart(date);
        } else {
          setEnd(date);
        }
      }
    },
    [start, end]
  );

  // refs for cell elements keyed by ISO date
  const cellRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // move DOM focus to focusedDate when it changes
  useEffect(() => {
    if (!focusedDate) return;
    const el = cellRefs.current.get(focusedDate.toISOString());
    if (el) {
      // Avoid forcing a scroll when the component mounts or when focus
      // changes programmatically (prevents jump-to-calendar on page reload).
      // Modern browsers support the preventScroll option.
      try {
        el.focus({ preventScroll: true });
      } catch {
        // Fallback for older browsers
        el.focus();
      }
    }
  }, [focusedDate]);

  // initialize focusedDate to start or a day in current month
  useEffect(() => {
    if (start) {
      setFocusedDate(start);
    } else {
      const today = startOfDay(new Date());
      const todayInView = cells.find((c) => !c.out && sameDay(c.date, today))?.date;
      if (todayInView) setFocusedDate(todayInView);
      else {
        const firstInMonth = cells.find((c) => !c.out)?.date || new Date();
        setFocusedDate(firstInMonth);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor]);

  const moveFocusBy = useCallback(
    (base: Date, deltaDays: number) => {
      const next = new Date(base);
      next.setDate(next.getDate() + deltaDays);
      if (next.getMonth() !== cursor.getMonth() || next.getFullYear() !== cursor.getFullYear()) {
        setCursor(new Date(next.getFullYear(), next.getMonth(), 1));
      }
      setFocusedDate(next);
    },
    [cursor]
  );

  const onGridKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!focusedDate) return;
      const key = event.key;
      let handled = true;
      if (key === 'ArrowLeft') moveFocusBy(focusedDate, -1);
      else if (key === 'ArrowRight') moveFocusBy(focusedDate, 1);
      else if (key === 'ArrowUp') moveFocusBy(focusedDate, -7);
      else if (key === 'ArrowDown') moveFocusBy(focusedDate, 7);
      else if (key === 'Home') {
        const day = focusedDate.getDay();
        moveFocusBy(focusedDate, -day);
      } else if (key === 'End') {
        const day = focusedDate.getDay();
        moveFocusBy(focusedDate, 6 - day);
      } else if (key === 'PageUp') {
        setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
        setFocusedDate((d) =>
          d ? new Date(d.getFullYear(), d.getMonth() - 1, d.getDate()) : null
        );
      } else if (key === 'PageDown') {
        setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
        setFocusedDate((d) =>
          d ? new Date(d.getFullYear(), d.getMonth() + 1, d.getDate()) : null
        );
      } else if (key === 'Enter' || key === ' ') {
        onCellClick(focusedDate);
      } else handled = false;

      if (handled) event.preventDefault();
    },
    [focusedDate, moveFocusBy, onCellClick]
  );

  const onPreset = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const type = event.currentTarget.getAttribute('data-preset');
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    let startDate = new Date(now);
    const endDate = new Date(now);
    if (type === 'mtd') {
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    } else {
      const n = Number(type);
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - (n - 1));
    }
    setStart(startDate);
    setEnd(endDate);
    setCursor(new Date(endDate.getFullYear(), endDate.getMonth(), 1));
  }, []);

  const onClear = useCallback(() => {
    setStart(null);
    setEnd(null);
  }, []);

  const today = useMemo(() => startOfDay(new Date()), []);

  return (
    <Card className='drp' data-daterange id='drp1'>
      <div className='drp-inputs'>
        <Input id='drpStart' placeholder='Start' readOnly value={fmt(start)} />
        <span className='separator'></span>
        <Input id='drpEnd' placeholder='End' readOnly value={fmt(end)} />
        <Button label='Clear' className='btn-outline-tertiary' id='drpClear' onClick={onClear} />
      </div>

      <div className='drp-presets'>
        <Button className='chip' data-preset='7' label='Last 7 days' onClick={onPreset} />
        <Button className='chip' data-preset='30' label='Last 30 days' onClick={onPreset} />
        <Button className='chip' data-preset='90' label='Last 90 days' onClick={onPreset} />
        <Button className='chip' data-preset='mtd' label='Month to date' onClick={onPreset} />
      </div>

      <div className='drp-cal'>
        <div className='drp-nav'>
          <Button label='‹' className='btn-outline-tertiary btn-sm' data-prev onClick={onPrev} />
          <b className='drp-title'>
            {cursor.toLocaleString(undefined, {
              month: 'long',
              year: 'numeric',
            })}
          </b>
          <Button label='›' className='btn-outline-tertiary btn-sm' data-next onClick={onNext} />
        </div>
        <div
          className='drp-dates'
          role='grid'
          tabIndex={0}
          aria-label='Calendar'
          aria-describedby='drp-instructions'
          onKeyDown={onGridKeyDown}
        >
          <div role='row' className='drp-row drp-head-row' aria-rowindex={1}>
            {DAY_NAMES.map((n, idx) => (
              <div key={n} role='columnheader' aria-colindex={idx + 1} className='drp-cell'>
                {n}
              </div>
            ))}
          </div>
          {(() => {
            const weeks: Cell[][] = [];
            for (let i = 0; i < cells.length; i += 7) {
              weeks.push(cells.slice(i, i + 7));
            }
            return weeks.map((week, rowIndex) => (
              <div role='row' key={rowIndex} className='drp-row' aria-rowindex={rowIndex + 2}>
                {week.map(({ date, out }, colIndex) => {
                  const classes = ['drp-cell'];
                  if (out) classes.push('drp-out');
                  if (sameDay(date, today)) classes.push('drp-today');
                  if (sameDay(date, start) || sameDay(date, end)) classes.push('drp-selected');
                  if (isBetween(date, start, end)) classes.push('drp-inrange');

                  const iso = date.toISOString();
                  const isFocused = focusedDate ? sameDay(date, focusedDate) : false;
                  const isSelected = sameDay(date, start) || sameDay(date, end);

                  return (
                    <div
                      key={iso}
                      ref={(el) => {
                        if (el) cellRefs.current.set(iso, el);
                        else cellRefs.current.delete(iso);
                      }}
                      role='gridcell'
                      aria-colindex={colIndex + 1}
                      aria-label={date.toLocaleString(undefined, {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                      tabIndex={isFocused ? 0 : -1}
                      aria-selected={isSelected}
                      aria-disabled={out}
                      className={classes.join(' ')}
                      data-date={iso}
                      onClick={() => !out && onCellClick(date)}
                    >
                      {date.getDate()}
                    </div>
                  );
                })}
              </div>
            ));
          })()}
        </div>
      </div>
    </Card>
  );
};

export default DateRangePicker;
